import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Info, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import QuotaBanner from "@/components/QuotaBanner";
import EmailVerifyNotice from "@/components/EmailVerifyNotice";
import { useAuth } from "@/context/AuthContext";

const euro = (n) => n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });

const PackageCard = ({ p, busy, locked, onBuy }) => (
  <div className="flex items-center justify-between gap-3 rounded-2xl border border-[#E2DCD0] bg-white p-4" data-testid={`package-${p.id}`}>
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#B39257]">Prezzo di lancio</p>
      <p className="font-serif text-2xl font-semibold text-[#0D2517]">{p.credits} analisi</p>
      <p className="text-sm text-[#5b6b60]" data-testid={`package-${p.id}-price`}>{euro(p.price)} · IVA inclusa</p>
    </div>
    <button disabled={busy || locked} onClick={() => onBuy(p.id)} className="btn-primary shrink-0 !px-5 !py-2.5 text-xs" data-testid={`package-${p.id}-buy`}>
      {busy ? "Attendere…" : "ACQUISTA"}
    </button>
  </div>
);

export default function BuyCredits() {
  const { user } = useAuth();
  const unverified = user?.email_verified === false;
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [unlimited, setUnlimited] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [params] = useSearchParams();
  useEffect(() => {
    api.get("/me/quota").then(({ data }) => {
      setUnlimited(data.unlimited);
      if (!data.unlimited) api.get("/payments/packages").then((r) => { setPackages(r.data); setLoaded(true); });
    });
  }, []);
  useEffect(() => { if (params.get("annullato")) toast.info("Pagamento annullato: non è stato addebitato nulla."); }, [params]);

  const buy = async (package_id) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { package_id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 px-5 py-6" data-testid="buy-credits-page">
      <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#c9a96e] text-[#0d2517]"><ShoppingBag /></span>
      <div>
        <p className="eyebrow">Analisi disponibili</p>
        <h1 className="font-serif text-3xl font-semibold text-[#0d2517]">Acquista nuove analisi</h1>
      </div>
      <QuotaBanner hideBuy />
      <EmailVerifyNotice />
      {packages.map((p) => <PackageCard key={p.id} p={p} busy={busy} locked={unverified} onBuy={buy} />)}
      {loaded && !unlimited && packages.length === 0 && <p className="text-sm text-[#5b6b60]" data-testid="buy-no-packages">Al momento non ci sono pacchetti disponibili.</p>}
      {!unlimited && <div className="flex gap-3 rounded-xl bg-[#F7F4EE] p-4 text-xs text-[#2D3A30]" data-testid="buy-credits-info">
        <Info size={16} className="shrink-0 text-[#B39257]" />
        <div className="space-y-1.5">
          <p>Acquisti crediti per le <strong>analisi automatizzate dell'app</strong>. Non comprendono la consulenza professionale della Dott.ssa Luigia De Vanna.</p>
          <p>I crediti non hanno scadenza e si sommano a quelli già disponibili. Pagamento unico: nessun abbonamento o rinnovo automatico.</p>
          <p>Il pagamento avviene sulla pagina sicura di Stripe. Le analisi vengono accreditate automaticamente dopo la conferma del pagamento.</p>
        </div>
      </div>}
    </div>
  );
}
